/**
 * Note3SegmentVM — Reactive segment ViewModel for note3 transcript display.
 *
 * Built with the same factory (createSolidViewModel + createTypeBoxAdapter)
 * as live-tranz's SegmentVM, but adapted for note3's thread and at-path layout.
 *
 * Each property is a lazy reactive getter backed by liveEntityAt — when applogs
 * arrive for an attribute the signal updates and any tracking memo recomputes.
 *
 * This fixes the ordering issue: the old per-entity LWW accumulator could drop
 * segment/startMs when it had a lower timestamp than a later attribute.
 * The ViewModel tracks each attribute independently (per-attribute LWW).
 */

import { Type } from '@sinclair/typebox'
import { createTypeBoxAdapter } from '@wovin/core/viewmodel/adapters/typebox'
import { createSolidViewModel } from '@wovin/ui-solid/viewmodel'

// ── Schema matching note3 segment applog layout ────────────────────────
const Note3SegmentSchema = Type.Object({
  media: Type.String(),
  startMs: Type.Optional(Type.String()),
  endMs: Type.Optional(Type.String()),
  transcript: Type.Optional(Type.String()),
  diarization: Type.Optional(Type.String()),
  speakerId: Type.Optional(Type.String()),
  speakerConfidence: Type.Optional(Type.String()),
  speakerSource: Type.Optional(Type.String()),
  channelDominance: Type.Optional(Type.String()),
  isDeleted: Type.Optional(Type.String()),
  isEdited: Type.Optional(Type.String()),
})

const AT_OVERRIDES: Record<string, string> = {
  media: 'segment/media',
  startMs: 'segment/startMs',
  endMs: 'segment/endMs',
  transcript: 'annotation/transcript',
  diarization: 'annotation/diarization',
  speakerId: 'annotation/speakerId',
  speakerConfidence: 'annotation/speakerConfidence',
  speakerSource: 'annotation/speakerSource',
  channelDominance: 'annotation/channelDominance',
  isDeleted: 'annotation/isDeleted',
  isEdited: 'annotation/isEdited',
}

export const Note3SegmentVM = createSolidViewModel({
  adapter: createTypeBoxAdapter(Note3SegmentSchema, 'segment', { atOverrides: AT_OVERRIDES }),
  vmName: 'Note3Segment',
})
